import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { ListIcon, ShoppingBagIcon, StoreIcon, TrendingUpIcon } from 'lucide-react';
import { useState } from 'react';

import {
  ChannelDTO,
  ListingDTO,
  ListingStatus,
  ListingStrategy,
} from '@invenco/common-interface/sales';

import { Badge } from '@/components/core/badge';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  DataTableViewMenu,
  useDataTable,
  useDataTableState,
  useDataTableViews,
  View,
} from '@/components/data-table';
import {
  DataTableAction,
  DataTableActionMenu,
} from '@/components/data-table/data-table-action-menu';
import { FormDialog } from '@/components/forms/form-dialog';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { DATA_POLLING_DURATION_MS, LIVE_DATA_INTERVAL_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { ListingSortField, QueryListingsProps } from '@/gateways/impl/SalesGateway';
import { fetchAllPages, formatNumber } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { usePulseFlag } from '@/shared/hooks/use-pulse-flag';
import { useBatchedRequests } from '@/shared/hooks/useBatchedRequests';
import { listingStatusType } from '@/shared/status-maps';
import { listingStatusTitle, listingStrategyTitle } from '@/shared/title-maps';

import { EditListingsDialogContent } from './edit-listings-dialog-content';
import { ListingListItem, ListingListMapper } from './listing-list-mapper';

const columnHelper = createColumnHelper<ListingListItem>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue, row }) => (
      <div className="flex min-w-0 items-center gap-2">
        <Badge className="max-w-full min-w-0 shrink">
          <span className="min-w-0 truncate">{getValue()}</span>
        </Badge>
        {row.original.externalSkuName ? (
          <Badge variant="secondary">({row.original.externalSkuName})</Badge>
        ) : null}
        <span className="min-w-0 truncate text-sm">{row.original.description}</span>
      </div>
    ),
    meta: { truncate: true },
  }),
  columnHelper.accessor('channelName', { header: 'Channel' }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return status ? (
        <StatusBadge status={status} type={listingStatusType} title={listingStatusTitle} />
      ) : null;
    },
  }),
  columnHelper.accessor('qtyExpected', {
    header: 'Expected',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyAvailable', {
    header: 'Available',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('listingStrategy', {
    header: 'Strategy',
    cell: ({ getValue }) => {
      const strategy = getValue();
      return strategy ? (
        <Badge variant="secondary">{listingStrategyTitle[strategy] || strategy}</Badge>
      ) : null;
    },
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyListed', {
    header: 'Listed',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
];

export const presetViews: View[] = [
  { id: 'all', name: 'All' },
  {
    id: ListingStatus.ACTIVE,
    name: listingStatusTitle[ListingStatus.ACTIVE],
    state: {
      filters: [{ id: 'status', operator: 'in', value: [ListingStatus.ACTIVE] }],
    },
  },
  {
    id: ListingStatus.DRAFT,
    name: listingStatusTitle[ListingStatus.DRAFT],
    state: {
      filters: [{ id: 'status', operator: 'in', value: [ListingStatus.DRAFT] }],
    },
  },
  {
    id: ListingStatus.ARCHIVED,
    name: listingStatusTitle[ListingStatus.ARCHIVED],
    state: {
      filters: [{ id: 'status', operator: 'in', value: [ListingStatus.ARCHIVED] }],
    },
  },
];

export const tieBreakerSort = [
  { key: 'createdAt', direction: 'asc' },
  { key: 'name', direction: 'asc' },
] as const;

const selectChannelNames = (data: ChannelDTO[]) => data.map((c) => c.name);

export function ListingList() {
  const { salesGateway } = useGateways();
  const [isLive, setIsLive] = useState(false);
  const [isPolling, triggerPolling] = usePulseFlag(DATA_POLLING_DURATION_MS);

  const { views, isLoadingSavedViews, ...viewTabsProps } = useDataTableViews({
    tableId: 'listings',
    presetViews,
  });

  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) => salesGateway.getChannels({ cursor, take }, { signal })),
    select: selectChannelNames,
  });

  const { state, currentView, onChangeViewId, filters, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {
      status: {
        label: 'Status',
        variant: 'multiselect',
        icon: ListIcon,
        options: [ListingStatus.ACTIVE, ListingStatus.DRAFT, ListingStatus.ARCHIVED],
        optionLabel: (option) => listingStatusTitle[option as ListingStatus] || option,
      },
      listingStrategy: {
        label: 'Strategy',
        variant: 'multiselect',
        icon: TrendingUpIcon,
        options: [
          ListingStrategy.DYNAMIC_AVAILABLE,
          ListingStrategy.PRESELL,
          ListingStrategy.SOLD_OUT,
        ],
        optionLabel: (option) => listingStrategyTitle[option as ListingStrategy] || option,
      },
      channelName: {
        label: 'Channel',
        variant: 'multiselect',
        icon: StoreIcon,
        options: channels ?? [],
      },
    },
    views,
    isLoadingSavedViews,
  });

  const queryProps: QueryListingsProps = {
    take: state.pagination.pageSize,
    offset: state.pagination.pageIndex * state.pagination.pageSize,
    sortBy: [
      ...state.sorting.map((s) => ({
        key: s.id as ListingSortField,
        direction: s.desc ? ('desc' as const) : ('asc' as const),
      })),
      ...tieBreakerSort,
    ],
    search: filters.search,
    filters: filters.getActiveFilterMap(),
  };

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'listings',
    input: queryProps,
    query: (input, { signal }) => salesGateway.queryListings(input, { signal }),
    select: ListingListMapper.toData,
    refetchInterval: isLive || isPolling ? LIVE_DATA_INTERVAL_MS : undefined,
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    columns,
    data: data?.data ?? [],
    rowCount: data?.total,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    state,
    enableRowSelection: true,
    ...tableProps,
  });

  const { run: bulkRefresh, isRunning: isRefreshing } = useBatchedRequests<
    ListingListItem,
    ListingDTO
  >({
    messageKey: 'listings-refresh',
    progressMessage: ({ results, failures, total, isRunning }) =>
      `${isRunning ? 'Refreshing' : 'Refreshed'} listings (${results.length}/${total}${
        failures.length ? ` - ${failures.length} failed` : ''
      })`,
  });

  const { run: bulkUpdate, isRunning: isUpdating } = useBatchedRequests<
    ListingListItem,
    ListingDTO
  >({
    messageKey: 'listings-update',
    progressMessage: ({ results, failures, total, isRunning: running }) =>
      `${running ? 'Updating' : 'Updated'} listings (${results.length}/${total}${
        failures.length ? ` - ${failures.length} failed` : ''
      })`,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <ShoppingBagIcon />
        </HeaderIcon>
        <HeaderTitle>Listings</HeaderTitle>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableViewMenu
            views={views}
            currentView={currentView}
            filterState={filters.filterState}
            rowCount={data?.total}
            onChangeViewId={onChangeViewId}
            {...viewTabsProps}
          />
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} isLive={isLive} onChangeLive={setIsLive} />
        </DataTableToolbar>
        <DataTable table={table} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
      <DataTableActionMenu table={table}>
        {(selectedRows) => {
          const isEditable =
            selectedRows.length > 0 &&
            new Set(selectedRows.map((listing) => `${listing.status}-${listing.listingStrategy}`))
              .size === 1;
          return (
            <>
              {isEditable && (
                <FormDialog>
                  <DialogTrigger asChild>
                    <DataTableAction disabled={isUpdating}>Edit</DataTableAction>
                  </DialogTrigger>
                  <EditListingsDialogContent
                    selectedListings={selectedRows}
                    onSubmit={async ({ status, strategy }) => {
                      await bulkUpdate({
                        execute: (listing) =>
                          salesGateway.updateListing({
                            id: listing.id,
                            status,
                            strategy,
                          }),
                        inputs: selectedRows,
                      });
                      void refetch();
                      table.resetRowSelection();
                      triggerPolling();
                    }}
                  />
                </FormDialog>
              )}

              <DataTableAction
                disabled={isRefreshing}
                onClick={async () => {
                  await bulkRefresh({
                    execute: ({ id }) => salesGateway.refreshListing(id),
                    inputs: selectedRows,
                  });
                  void refetch();
                  table.resetRowSelection();
                  triggerPolling();
                }}
              >
                Refresh
              </DataTableAction>
            </>
          );
        }}
      </DataTableActionMenu>
    </Page>
  );
}
